import { ChevronLeftIcon } from "@heroicons/react/outline";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Field, FormikHelpers, FormikProps } from "formik";
import { every, toLower, words } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as Yup from "yup";
import CenteredLoading from "../../../components/CenteredLoading";
import CollapsedBox from "../../../components/CollapsedBox";
import Checkbox from "../../../components/forms/Checkbox";
import DropdownSearch from "../../../components/forms/DropdownSearch";
import FormProvider from "../../../components/forms/FormProvider";
import Input from "../../../components/forms/Input";
import { useAppearance } from "../../../context/appearance";
import { useAuthentication } from "../../../context/authentication";
import { useBillingPlans } from "../../../context/billingPlans";
import { useToast } from "../../../context/toast";
import { useWorkspaces } from "../../../context/workspaces";
import classNames from "../../../utils/classNames";
import {
  CardPaymentMethod,
  PaymentStep,
  StripeTax
} from "../../../utils/types";
import CardList from "../../Workspace/Settings/Billing/BillingPaymentMethod/CardList";

interface Props {
  onComplete: (paymentMethod?: CardPaymentMethod) => void;
  current: boolean;
  onBack: () => void;
  className?: string;
  title?: string | JSX.Element;
  withCollapse?: boolean;
  withCardList?: boolean;
}

const PaymentMethod: React.FC<Props> = ({
  onComplete,
  current,
  onBack,
  className,
  title,
  withCollapse = true,
  withCardList = false
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addToast } = useToast();
  const { isDark } = useAppearance();
  const {
    loading,
    clientSecret,
    newPlan,
    updatePaymentMethod,
    updateBillingDetails,
    setPaymentStep,
    createPaymentIntent,
    setClientSecret
  } = useBillingPlans();
  const { user } = useAuthentication();
  const { currentWorkspace, fetchWorkspaceById } = useWorkspaces();
  const elements = useElements();
  const stripe = useStripe();
  const [loadingConfirmCardSetup, setLoadingConfirmCardSetup] = useState(false);
  const [showCardList, setShowCardList] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardInputRef = useRef<HTMLDivElement | null>(null);
  const [countries, setCountries] =
    useState<{ name: string; code: string }[]>();
  const [stripeTax, setStripeTax] = useState<StripeTax[]>();

  useEffect(() => {
    import("../../../utils/countries").then(({ countries }) => {
      setCountries(countries);
    });

    import("../../../utils/stripeTax").then(({ stripeTax }) => {
      setStripeTax(stripeTax);
    });
  }, []);

  useEffect(() => {
    setShowCardList(withCardList);
  }, [withCardList]);

  useEffect(() => {
    if (!currentWorkspace || showCardList) return;

    createPaymentIntent();

    return () => {
      setClientSecret(undefined);
    };
  }, [createPaymentIntent, currentWorkspace, setClientSecret, showCardList]);

  const initialValues = useMemo(
    () => ({
      name:
        newPlan.paymentMethod?.card.name ??
        currentWorkspace?.billing_details?.billing_name ??
        "",
      taxDetails: !!currentWorkspace?.tax_value,
      taxType:
        (stripeTax &&
          stripeTax.find(
            (country) =>
              currentWorkspace &&
              country.external_type === currentWorkspace.tax_type
          )) ??
        undefined,
      taxId: currentWorkspace?.tax_value ?? "",
      email: user.email ?? "",
      country:
        (currentWorkspace?.billing_details?.country &&
          countries &&
          countries.find(
            (item) => item.code === currentWorkspace?.billing_details?.country
          )) ??
        "",
      state:
        newPlan.paymentMethod?.card.address.state ??
        currentWorkspace?.billing_details?.state ??
        "",
      city:
        newPlan.paymentMethod?.card.address.city ??
        currentWorkspace?.billing_details?.city ??
        "",
      address1:
        newPlan.paymentMethod?.card.address.line1 ??
        currentWorkspace?.billing_details?.line1 ??
        "",
      address2:
        newPlan.paymentMethod?.card.address.line2 ??
        currentWorkspace?.billing_details?.line2 ??
        "",
      postalCode:
        newPlan.paymentMethod?.card.address.postal_code ??
        currentWorkspace?.billing_details?.postal_code ??
        ""
    }),
    [
      newPlan.paymentMethod?.card.name,
      newPlan.paymentMethod?.card.address.state,
      newPlan.paymentMethod?.card.address.city,
      newPlan.paymentMethod?.card.address.line1,
      newPlan.paymentMethod?.card.address.line2,
      newPlan.paymentMethod?.card.address.postal_code,
      currentWorkspace,
      stripeTax,
      user.email,
      countries
    ]
  );

  const updateBilling = useCallback(
    async (formValues: FormValues) => {
      setErrorMessage(null);

      const data = {
        billing_name: formValues.name,
        billing_email: formValues.email,
        address: {
          line1: formValues.address1,
          line2: formValues.address2,
          postal_code: formValues.postalCode,
          city: formValues.city,
          state: formValues.state,
          country: formValues?.country.code
        },
        tax: {
          type: formValues.taxDetails
            ? formValues?.taxType.external_type
            : undefined,
          value: formValues.taxDetails ? formValues.taxId : undefined
        }
      };

      try {
        const response = await updateBillingDetails(data);

        if (response.status) {
          await fetchWorkspaceById(currentWorkspace?.uuid);

          addToast({
            type: "success",
            title: "Updated successfully"
          });

          return true;
        }

        addToast({
          type: "error",
          title: response.message || "Something went wrong"
        });

        return false;
      } catch (error: any) {
        setErrorMessage(error?.message);

        return false;
      }
    },
    [addToast, currentWorkspace?.uuid, fetchWorkspaceById, updateBillingDetails]
  );

  const updatePayment = useCallback(
    async (formValues: FormValues) => {
      if (!newPlan.paymentMethod) return;

      try {
        const response = await updatePaymentMethod({
          id: newPlan?.paymentMethod?.id,
          billing_name: formValues.name,
          billing_email: formValues.email,
          address: {
            city: formValues.city,
            country: formValues.country.code,
            line1: formValues.address1,
            line2: formValues.address2,
            postal_code: formValues.postalCode,
            state: formValues.state
          }
        });

        if (response.status) {
          setLoadingConfirmCardSetup(false);
          addToast({
            title: "Payment successfully updated",
            message:
              "Your payment method has been updated. You can now use it to pay for your plan.",
            type: "success"
          });

          onComplete();
          return;
        }

        setLoadingConfirmCardSetup(false);
        addToast({
          title: "Payment error",
          message:
            "There was an error with your payment method. Please try again.",
          type: "error"
        });
      } catch {
        setLoadingConfirmCardSetup(false);
        addToast({
          title: "Payment error",
          message:
            "There was an error with your payment method. Please try again.",
          type: "error"
        });
      }
    },
    [addToast, newPlan.paymentMethod, onComplete, updatePaymentMethod]
  );

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      if (formValues.taxDetails && !formValues.taxId && !formValues.taxType) {
        setErrorMessage("Please select a tax type");

        return;
      }

      const updateResponse = await updateBilling(formValues);

      if (!updateResponse) {
        formikHelpers.setSubmitting(false);

        return;
      }

      formikHelpers.setSubmitting(true);
      setErrorMessage(null);

      if (newPlan.paymentMethod?.id) {
        return updatePayment(formValues);
      }

      if (!elements || !clientSecret) return;

      const cardEmelent = elements.getElement(CardElement);

      if (!cardEmelent) return;

      setLoadingConfirmCardSetup(true);

      try {
        const response = await stripe?.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardEmelent,
            billing_details: {
              name: formValues.name,
              email: formValues.email,
              address: {
                city: formValues.city,
                country: formValues.country.code,
                line1: formValues.address1,
                line2: formValues.address2,
                postal_code: formValues.postalCode,
                state: formValues.state
              }
            }
          }
        });

        if (
          response?.setupIntent &&
          response?.setupIntent.payment_method &&
          response.setupIntent.status === "succeeded"
        ) {
          onComplete({
            id: response.setupIntent.payment_method,
            card: {
              address: {
                city: formValues.city,
                country: formValues.country.code,
                line1: formValues.address1,
                line2: formValues.address2,
                postal_code: formValues.postalCode,
                state: formValues.state
              },
              brand: "",
              name: formValues.name,
              email: formValues.email,
              exp_month: 0,
              exp_year: 0,
              last4: ""
            }
          });

          if (withCardList) {
            setShowCardList(true);
          }

          formikHelpers.setSubmitting(false);
          setLoadingConfirmCardSetup(false);

          return;
        }

        addToast({
          title: "Payment error",
          message:
            "There was an error with your payment method. Please try again.",
          type: "error"
        });

        cardInputRef.current?.scrollIntoView({ behavior: "smooth" });
        formikHelpers.setSubmitting(false);
        setLoadingConfirmCardSetup(false);
      } catch {
        cardInputRef.current?.scrollIntoView({ behavior: "smooth" });
        setLoadingConfirmCardSetup(false);
        addToast({
          title: "Payment error",
          message:
            "There was an error with your payment method. Please try again.",
          type: "error"
        });

        formikHelpers.setSubmitting(false);
      }
    },
    [
      addToast,
      clientSecret,
      elements,
      newPlan.paymentMethod?.id,
      onComplete,
      stripe,
      updateBilling,
      updatePayment,
      withCardList
    ]
  );

  const getCollapedBoxIcon = useMemo(() => {
    if (showCardList) return "3.";

    return (
      <ChevronLeftIcon
        onClick={() => {
          if (withCardList) {
            setShowCardList(true);

            return;
          }

          onBack();
        }}
        className="w-5 mr-3 cursor-pointer"
      />
    );
  }, [onBack, showCardList, withCardList]);

  useEffect(() => {
    if (!current) {
      return;
    }

    containerRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [current]);

  const getCountries = (searchInput: string | undefined) => {
    if (!countries) return [];

    const filteredCountries = countries.filter((country) => {
      if (searchInput) {
        const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(country.name).includes(word) ||
            toLower(country.code).includes(word)
        );
      }
      return true;
    });

    return filteredCountries;
  };

  const getStripeTax = (searchInput: string | undefined) => {
    if (!stripeTax) return [];

    const filteredStripeCountries = stripeTax.filter((country) => {
      if (searchInput) {
        const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(country.country).includes(word) ||
            toLower(country.description).includes(word)
        );
      }
      return true;
    });

    return filteredStripeCountries;
  };

  return (
    <div ref={containerRef}>
      <CollapsedBox
        className={classNames(className, "mt-5 lg:mt-8")}
        title={title || "Payment method"}
        icon={getCollapedBoxIcon}
        withCollapse={withCollapse}
        setOpened={() => setPaymentStep(PaymentStep.paymentMethod)}
        opened={current}
        loadingSave={loadingConfirmCardSetup}
      >
        <div className="py-5">
          {showCardList ? (
            <div className="flex flex-col">
              <div className="px-3 md:px-5">
                <CardList
                  onClickCard={(isNew) => isNew && setShowCardList(false)}
                />
              </div>

              <div className="flex items-center justify-end mt-6 px-3 md:px-5 border-t dark:border-gray-500 pt-5">
                <button
                  className="w-full md:w-auto mr-4 sm:mr-0 text-xs py-1.5 px-6 rounded dark:text-white border dark:border-transparent dark:bg-gray-700 dark:hover:opacity-75 outline-none focus:outline-none"
                  onClick={() => onBack()}
                >
                  Cancel
                </button>
                <button
                  disabled={!newPlan.paymentMethod || loadingConfirmCardSetup}
                  className="w-full md:w-auto text-xs md:ml-3 py-1.5 px-8 rounded border text-white bg-hotspotty-400 hover:bg-hotspotty-400 border-hotspotty-400 outline-none focus:outline-none disabled:opacity-75 disabled:hover:cursor-default disabled:hover:bg-hotspotty-400"
                  onClick={() => {
                    if (!newPlan.paymentMethod || loadingConfirmCardSetup)
                      return;

                    onComplete();
                  }}
                >
                  {loadingConfirmCardSetup ? (
                    <span className="flex items-center">
                      <CenteredLoading className="w-4 h-4 mr-2" /> Continue...
                    </span>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="px-3 md:px-5">
              <span className="block text-sm font-semibold dark:text-gray-300 mb-4 pb-2 border-b dark:border-gray-500">
                Payment
              </span>
              <div>
                {!newPlan.paymentMethod?.id ? (
                  <div ref={cardInputRef}>
                    <div className="rounded-md bg-gray-100 dark:bg-gray-700 dark:text-white pl-2 mb-5 overflow-hidden">
                      <CardElement
                        options={{
                          style: {
                            base: {
                              backgroundColor: isDark ? "#374151" : "#F3F4F6",
                              padding: "20px 20px",
                              fontSize: "14px",
                              lineHeight: "40px",
                              color: isDark ? "#fff" : "#000",
                              "::placeholder": {
                                color: isDark ? "#9CA3AF" : "#b5bac4"
                              }
                            },
                            invalid: {
                              color: "#f35348",
                              iconColor: "#f35348"
                            }
                          },
                          hidePostalCode: true,
                          iconStyle: "solid"
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <input
                    disabled={true}
                    className="bg-gray-100 h-10 px-2 mb-5 block w-full shadow-sm text-xs md:text-sm rounded-md transition duration-150 ease-in-out pr-10 dark:bg-gray-700 dark:text-gray-200 opacity-60"
                    value={`xxxx xxxx xxxx ${newPlan.paymentMethod.card.last4}`}
                  />
                )}

                <span className="text-xs -mt-2 mb-4 block">
                  Payments are processed securely through{" "}
                  <a
                    href="https://stripe.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-hotspotty-400 dark:text-hotspotty-300 font-bold"
                  >
                    Stripe.
                  </a>
                </span>

                <FormProvider
                  error={errorMessage}
                  submitButtonText="Save"
                  classnames={{
                    wrapper: "md:max-w-none w-full pt-0",
                    form: "dark:bg-transparent",
                    buttonWrapper: "-mr-4 pb-0",
                    cancelButton:
                      "text-xs px-10 dark:text-white border h-8 dark:hover:border-transparent dark:border-transparent dark:bg-gray-700 dark:hover:opacity-75 outline-none focus:outline-none",
                    submitButton:
                      "bg-hotspotty-400 hover:bg-hotspotty-400 border-hotspotty-400 px-10 h-8 text-xs"
                  }}
                  onCancel={() => {
                    if (withCardList) {
                      setShowCardList(true);

                      return;
                    }

                    onBack();
                  }}
                  formikProps={{
                    validateOnChange: false,
                    validateOnBlur: false,
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape({
                      name: Yup.string().required("Required"),
                      email: Yup.string()
                        .email("Please enter a valid email address")
                        .required("Required"),
                      country: Yup.object().required("Required"),
                      state: Yup.string().required("Required"),
                      city: Yup.string().required("Required"),
                      address1: Yup.string().required("Required"),
                      address2: Yup.string(),
                      postalCode: Yup.string().required("Required")
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    values: FormValues
                  }: FormikProps<FormValues>) => {
                    return (
                      <>
                        <span className="block text-sm font-semibold dark:text-gray-300 mb-2">
                          Billing details
                        </span>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">Name</p>
                            <span className="text-xs dark:text-gray-200">
                              Full name or business name
                            </span>
                          </div>
                          <Field
                            name="name"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={isSubmitting}
                          />
                        </div>

                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">
                              Tax details
                            </p>
                            <span className="text-xs dark:text-gray-200">
                              Add company tax details
                            </span>
                          </div>
                          <div className="flex flex-col w-full md:w-6/12">
                            <Field
                              name="taxDetails"
                              component={Checkbox}
                              label="Include tax details"
                              className="flex w-full mt-0"
                              disabled={isSubmitting || loading.paymentMethods}
                            />
                            {FormValues.taxDetails && (
                              <div className="flex md:flex-row flex-col mt-2">
                                <Field
                                  name="taxType"
                                  component={DropdownSearch}
                                  placeholder="Select Tax region"
                                  classnames={{
                                    input:
                                      "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none border-0",
                                    wrapper:
                                      "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                                  }}
                                  withoutMinimumSearchCharacters
                                  fetchItems={getStripeTax}
                                  initialItems={getStripeTax}
                                  itemToString={(item: StripeTax) =>
                                    `${item.country} - ${item.description}`
                                  }
                                  disabled={isSubmitting}
                                />
                                <Field
                                  name="taxId"
                                  component={Input}
                                  placeholder="Tax Number"
                                  classnames={{
                                    input:
                                      "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                                    wrapper:
                                      "w-full md:w-6/12 h-full mt-1.5 md:-mt-1 md:ml-2 ml-0"
                                  }}
                                  disabled={
                                    isSubmitting || loading.paymentMethods
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-5/12">
                            <p className="text-sm font-bold mb-1">
                              Billing Email
                            </p>
                            <span className="text-xs dark:text-gray-200">
                              Receipts and billing-related notifications will be
                              sent to this adress.
                            </span>
                          </div>
                          <Field
                            name="email"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={initialValues.email !== ""}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">Country</p>
                            <span className="text-xs dark:text-gray-200">
                              Billing address country
                            </span>
                          </div>
                          <Field
                            name="country"
                            component={DropdownSearch}
                            placeholder="Select country"
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none border-0",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            withoutMinimumSearchCharacters
                            fetchItems={getCountries}
                            initialItems={getCountries}
                            itemToString={(item: {
                              name: string;
                              code: string;
                            }) => item.name}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">State</p>
                            <span className="text-xs dark:text-gray-200">
                              State, province or region
                            </span>
                          </div>
                          <Field
                            name="state"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">City</p>
                            <span className="text-xs dark:text-gray-200">
                              City, district, suburb, town or village
                            </span>
                          </div>
                          <Field
                            name="city"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">Address 1</p>
                            <span className="text-xs dark:text-gray-200">
                              Street, PO Box, or company name
                            </span>
                          </div>
                          <Field
                            name="address1"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">Address 2</p>
                            <span className="text-xs dark:text-gray-200">
                              Apartment, Suite, Unit or building
                            </span>
                          </div>
                          <Field
                            name="address2"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center border-t dark:border-gray-500 py-4">
                          <div className="flex flex-col w-full md:w-6/12">
                            <p className="text-sm font-bold mb-1">
                              Postal Code
                            </p>
                            <span className="text-xs dark:text-gray-200">
                              ZIP or postal code
                            </span>
                          </div>
                          <Field
                            name="postalCode"
                            component={Input}
                            classnames={{
                              input:
                                "bg-gray-100 dark:bg-gray-700 h-10 px-2 outline-none",
                              wrapper: "w-full md:w-6/12 h-full mt-1.5 md:-mt-1"
                            }}
                            disabled={isSubmitting}
                          />
                        </div>
                      </>
                    );
                  }}
                </FormProvider>
              </div>
            </div>
          )}
        </div>
      </CollapsedBox>
    </div>
  );
};

export default PaymentMethod;

//
// Utils
//

interface FormValues {
  name: string;
  taxDetails: boolean;
  taxId: string;
  taxType: StripeTax;
  email: string;
  country: { name: string; code: string };
  state: string;
  city: string;
  address1: string;
  address2: string;
  postalCode: string;
}

const ORDERED_FIELD_NAMES = [
  "name",
  "taxDetails",
  "taxType",
  "taxId",
  "email",
  "country",
  "state",
  "city",
  "address1",
  "address2",
  "postalCode"
];
